import { ReactNode, createContext, useCallback, useContext, useRef, useState } from 'react';
import PopupRounded from '@bridebook/web/components/generic/popup-rounded/popup-rounded';

interface PopupRoundedModalContextType {
  openModal: (id: string, content: ReactNode) => void;
  closeModal: () => void;
  modalContent: ReactNode | null;
}

const PopupRoundedModalContext = createContext<PopupRoundedModalContextType | undefined>(undefined);

/**
 * Generic modal context that provides base modal functionality.
 * This context is not meant to be used directly - instead use the useInterceptModal hook that uses this context and provides
 * a more specific implementation with standardized UI and behavior.
 * There is no padding on the modal itself - you need to use it with some content that has padding.
 * This is abstracted to let us use the same provider if ever needed for some other modals. It accepts
 * a generic ReactNode as content as uses the PopupRounded component to render the modal, its size is in sync with the design system.
 */

export const PopupRoundedModalProvider = ({ children }: { children: ReactNode }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const modalContent = useRef<ReactNode | null>(null);
  const modalId = useRef<string>('');

  const openModal = useCallback((id: string, content: ReactNode) => {
    modalContent.current = content;
    modalId.current = id;
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    if (modalId.current) {
      setIsModalOpen(false);
      modalId.current = '';
      modalContent.current = null;
    }
  }, [modalId]);

  return (
    <PopupRoundedModalContext.Provider
      value={{
        openModal,
        closeModal,
        modalContent: modalContent.current,
      }}>
      {children}
      {isModalOpen && (
        <PopupRounded
          id={modalId.current}
          show={isModalOpen}
          onClose={closeModal}
          modalStyle={{
            maxWidth: '100%',
            paddingTop: 0,
            paddingHorizontal: 0,
            paddingBottom: 0,
          }}>
          {modalContent.current}
        </PopupRounded>
      )}
    </PopupRoundedModalContext.Provider>
  );
};

export const usePopupRoundedModal = () => {
  const context = useContext(PopupRoundedModalContext);
  if (!context) {
    throw new Error('usePopupRoundedModal must be used within PopupRoundedModalProvider');
  }
  return context;
};
